import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Unsubscribe() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [message, setMessage] = useState(t('unsubscribe.processing'));

    const unsubscribeMutation = useMutation({
        mutationFn: async (token: string) => {
            return await apiRequest("POST", "/api/auth/unsubscribe", { token });
        },
        onSuccess: () => {
            setStatus("success");
            setMessage(t('unsubscribe.successMessage'));
        },
        onError: (error: any) => {
            setStatus("error");
            setMessage(error.message || t('unsubscribe.errorMessage'));
        },
    });

    useEffect(() => {
        if (token) {
            unsubscribeMutation.mutate(token);
        } else {
            setStatus("error");
            setMessage(t('unsubscribe.invalidLink'));
        }
    }, [token, t]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle>{t('unsubscribe.title')}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center space-y-4 text-center">
                    {status === "loading" && (
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    )}

                    {status === "success" && (
                        <>
                            <CheckCircle className="w-16 h-16 text-green-500" />
                            <p className="text-lg font-medium text-gray-900">{t('unsubscribe.success')}</p>
                            <p className="text-gray-500">{message}</p>
                            <p className="text-sm text-gray-400">
                                {t('unsubscribe.successHint')}
                            </p>
                        </>
                    )}

                    {status === "error" && (
                        <>
                            <XCircle className="w-16 h-16 text-red-500" />
                            <p className="text-lg font-medium text-gray-900">{t('unsubscribe.error')}</p>
                            <p className="text-gray-500">{message}</p>
                        </>
                    )}

                    <div className="pt-4">
                        <Link to="/">
                            <Button>{t('unsubscribe.returnHome')}</Button>
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
